/**
 * Trovva — Client Invite Panel
 *
 * Renders inside the Deal Room tab of DealDetailDrawer.
 * Agent can:
 *   1. Enter client email + name + role (buyer/seller)
 *   2. Select which documents to share
 *   3. Send magic link invite
 *   4. See existing invites with status, revoke access
 *
 * Read-only portal: client sees deal progress, timeline, shared docs.
 * No signing, no editing, no attorney access.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Send, Trash2, Check, Copy, Eye, Loader2,
  Shield, FileText, Clock, Mail, AlertCircle, RefreshCw,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDate } from '../../lib/dateFormat';

const card = {
  background: theme.colors.card,
  borderRadius: theme.radius.lg,
  border: `1px solid ${theme.colors.border}`,
  padding: 16,
  marginBottom: 12,
};
const label = { display: 'block', fontSize: 12, fontWeight: 500, color: theme.colors.textLight, marginBottom: 4 };
const inputStyle = {
  width: '100%', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
  padding: '7px 10px', fontSize: 13, color: theme.colors.text, background: theme.colors.card,
  outline: 'none', boxSizing: 'border-box',
};
const btnPrimary = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', background: theme.colors.primary, color: '#fff', border: 'none',
  borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

export default function ClientInvitePanel({ dealId, showToast }) {
  const [invites, setInvites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(null);

  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('buyer');
  const [selectedDocs, setSelectedDocs] = useState([]);

  const getHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
  };

  const fetchInvites = useCallback(async () => {
    const h = await getHeaders();
    if (!h) return;
    try {
      const resp = await fetch(`/api/client-invite?deal_id=${dealId}`, { headers: h });
      if (resp.ok) {
        const data = await resp.json();
        setInvites(data.invites || []);
      }
    } catch (e) { /* silent */ }
  }, [dealId]);

  const fetchDocuments = useCallback(async () => {
    const h = await getHeaders();
    if (!h) return;
    try {
      const resp = await fetch(`/api/deal-documents?deal_id=${dealId}`, { headers: h });
      if (resp.ok) {
        const data = await resp.json();
        setDocuments(data.documents || []);
      }
    } catch (e) { /* silent */ }
  }, [dealId]);

  useEffect(() => {
    Promise.all([fetchInvites(), fetchDocuments()]).finally(() => setLoading(false));
  }, [fetchInvites, fetchDocuments]);

  const sendInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const h = await getHeaders();
      const resp = await fetch('/api/client-invite', {
        method: 'POST',
        headers: h,
        body: JSON.stringify({
          deal_id: dealId,
          client_email: email.trim(),
          client_name: name.trim() || null,
          client_role: role,
          shared_document_ids: selectedDocs,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      if (showToast) showToast(data.message || 'Invite sent!');
      setEmail(''); setName(''); setSelectedDocs([]); setShowForm(false);
      fetchInvites();
    } catch (e) {
      if (showToast) showToast('Error: ' + e.message);
    } finally { setSending(false); }
  };

  const revokeInvite = async (inviteId) => {
    const h = await getHeaders();
    try {
      await fetch(`/api/client-invite?invite_id=${inviteId}`, { method: 'DELETE', headers: h });
      fetchInvites();
      if (showToast) showToast('Access revoked');
    } catch (e) { /* silent */ }
  };

  const toggleDoc = (docId) => {
    setSelectedDocs(prev => prev.includes(docId) ? prev.filter(d => d !== docId) : [...prev, docId]);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 24, color: theme.colors.textLight }}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={16} style={{ color: theme.colors.primary }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Client Portal</span>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}>
            <UserPlus size={14} /> Invite Client
          </button>
        )}
      </div>

      <p style={{ fontSize: 12, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 14 }}>
        Give your client read-only access to see deal progress and shared documents. They receive a secure magic link via email.
      </p>

      {/* Invite Form */}
      {showForm && (
        <div style={{ ...card, borderColor: theme.colors.primary + '40', background: theme.colors.background }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Mail size={14} /> Send Client Invite
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={label}>Client email *</label>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="client@email.com" type="email" style={inputStyle} />
            </div>
            <div>
              <label style={label}>Client name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={label}>Client role</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['buyer', 'seller'].map(r => (
                <button key={r} onClick={() => setRole(r)} style={{
                  padding: '5px 14px', borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 500,
                  border: `1px solid ${role === r ? theme.colors.primary : theme.colors.border}`,
                  background: role === r ? theme.colors.primary + '12' : 'transparent',
                  color: role === r ? theme.colors.primary : theme.colors.text,
                  cursor: 'pointer',
                }}>{r.charAt(0).toUpperCase() + r.slice(1)}</button>
              ))}
            </div>
          </div>

          {/* Document selection */}
          {documents.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Share documents ({selectedDocs.length} selected)</label>
              <div style={{ maxHeight: 140, overflowY: 'auto', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm }}>
                {documents.map(doc => (
                  <label key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer',
                    borderBottom: `1px solid ${theme.colors.border}`, fontSize: 12,
                  }}>
                    <input type="checkbox" checked={selectedDocs.includes(doc.id)} onChange={() => toggleDoc(doc.id)} />
                    <FileText size={13} style={{ color: theme.colors.textLight }} />
                    <span style={{ color: theme.colors.text }}>{doc.file_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={sendInvite} disabled={sending || !email.trim()} style={{ ...btnPrimary, opacity: sending || !email.trim() ? 0.5 : 1 }}>
              {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
              {sending ? 'Sending...' : 'Send Invite'}
            </button>
            <button onClick={() => { setShowForm(false); setEmail(''); setName(''); setSelectedDocs([]); }} style={{
              padding: '6px 12px', background: 'transparent', border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm, fontSize: 12, color: theme.colors.text, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Existing Invites */}
      {invites.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.textLight, marginBottom: 8 }}>
            Active Invites ({invites.filter(i => i.status !== 'revoked').length})
          </div>
          {invites.map(inv => {
            const isActive = inv.status === 'active';
            const isPending = inv.status === 'pending';
            const isRevoked = inv.status === 'revoked';

            return (
              <div key={inv.id} style={{
                ...card, opacity: isRevoked ? 0.5 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>
                    {inv.client_name || inv.client_email}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, display: 'flex', gap: 8, alignItems: 'center', marginTop: 3 }}>
                    <span style={{
                      padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: isActive ? '#DCFCE7' : isPending ? '#FEF3C7' : '#FEE2E2',
                      color: isActive ? '#166534' : isPending ? '#92400E' : '#991B1B',
                    }}>{inv.status.toUpperCase()}</span>
                    <span>{inv.client_role}</span>
                    {inv.last_viewed_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Eye size={10} /> Last seen {formatDate(inv.last_viewed_at)}
                      </span>
                    )}
                  </div>
                </div>
                {!isRevoked && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => revokeInvite(inv.id)} title="Revoke access" style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: theme.colors.danger,
                    }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {invites.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: theme.colors.textLight, fontSize: 13 }}>
          No clients invited yet. Click "Invite Client" to share deal access.
        </div>
      )}
    </div>
  );
}
